import React, { useEffect, useState, useRef } from 'react'
import Orders from 'components/Panel/Reseller/Orders'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { orders, invalidateCache } from 'redux/reseller/orders/ordersSlice';
import Pagination from 'components/Pagination';


const OrdersContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data, total, pages, page, per_page } = useSelector(state => state.resellerOrders.orders);
  const { cacheTimestamp, isLoading } = useSelector(state => state.resellerOrders);
  const { balance_try } = useSelector(state => state.resellerProfile.profile);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [perPage, setPerPage] = useState(15);
  
  const fetchTimerRef = useRef(null);
  const cacheInvalidationTimerRef = useRef(null);
  const isBackgroundRefreshRef = useRef(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  // Reset to page 1 when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== search) return;
    if (debouncedSearch) {
      setCurrentPage(1);
    }
  }, [debouncedSearch]);

  // Check cache validity and fetch if needed
  const shouldFetchData = () => {
    if (!cacheTimestamp) return true;
    const cacheAge = Date.now() - cacheTimestamp;
    return cacheAge > 10000; // 10 seconds
  };

  const fetchOrders = (force = false) => {
    if (force || shouldFetchData()) {
      dispatch(orders({
        page: currentPage,
        per_page: perPage,
        list_subreseller_orders: true,
        status: filter,
        search: debouncedSearch || undefined
      }));
    }
  };

  // Main fetch effect - invalidate cache and force fetch when dependencies change
  useEffect(() => {
    dispatch(invalidateCache());
    isBackgroundRefreshRef.current = false;
    fetchOrders(true);
    
    // Set up auto-refresh every 10 seconds
    if (fetchTimerRef.current) {
      clearInterval(fetchTimerRef.current);
    }
    fetchTimerRef.current = setInterval(() => {
      isBackgroundRefreshRef.current = true;
      fetchOrders(false);
    }, 10000);
    
    return () => {
      if (fetchTimerRef.current) {
        clearInterval(fetchTimerRef.current);
      }
    };
  }, [currentPage, filter, debouncedSearch, perPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (value) => {
    setFilter(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
  };

  const handlePerPageChange = (value) => {
    setPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    dispatch(invalidateCache());
    isBackgroundRefreshRef.current = false;
    fetchOrders(true);
  };


  return (
    <div className='bg-black min-h-screen'>
      <div className='p-4 flex flex-col md:flex-row md:justify-between md:items-center gap-4'>
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <h2 className='text-xl font-semibold text-white-100'>Gelen Dekontlar</h2>
          <div className="text-white-100 text-lg font-semibold">
            Bakiye: {balance_try ? `${balance_try.toFixed(2)} TL` : '0.00 TL'}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-white-100 text-sm">Sayfa Başı:</label>
            <select
              value={perPage}
              onChange={(e) => handlePerPageChange(e.target.value)}
              className="px-3 py-1.5 rounded-md bg-gray-800 text-white-100 border border-gray-700 focus:outline-none focus:border-primary"
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <button
            onClick={handleRefresh}
            className='bg-secondary text-white-100 px-4 py-1.5 rounded-md cursor-pointer hover:bg-opacity-80 transition-all disabled:opacity-50 disabled:cursor-not-allowed'
            disabled={isLoading}
          >
            {isLoading ? 'Yükleniyor...' : 'Yenile'}
          </button>
        </div>
      </div>
      
      {/* Top Pagination */}
      {pages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={pages}
          onPageChange={handlePageChange}
          totalItems={total}
          perPage={perPage}
        />
      )}

      <Orders
        data={data}
        filter={filter}
        search={search}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        isLoading={isLoading && !isBackgroundRefreshRef.current}
        onRefresh={handleRefresh}
      />
      
      {/* Bottom Pagination */}
      {pages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={pages}
          onPageChange={handlePageChange}
          totalItems={total}
          perPage={perPage}
        />
      )}
    </div>
  )
}

export default OrdersContainer